import React, { useEffect } from "react";
import { FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { FormStack } from "../components/FormStack/FormStack";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { TextField } from "../components/fields/TextField/TextField";
import * as Yup from "yup";
import { UserFilterType } from "../components/Users/UserFilterQueryModal";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";

const emptyStrToNull = (value: string | null): string | null => {
  if (!value || value.trim() === "") {
    return null;
  } else {
    return value;
  }
};

interface UserFilterQueryFormValues {
  Query: UserFilterType;
  result?: string | undefined;
}

interface UserFilterQueryFormProps {
  apiClient: AdminApiClient;
  query?: UserFilterType;
  onCancel: () => void;
  onSuccess: (query?: UserFilterType) => void;
  onFailure?: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  isValid,
  onCancel,
  values,
  query,
  setFieldValue,
  setFieldError,
}: UserFilterQueryFormProps & FormikProps<UserFilterQueryFormValues>) => {
  const { data: businessOptions, isLoading: businessOptionsLoading } =
    useAdminApiRequest(
      (apiClient) => apiClient.getBusinesses(undefined, 1000),
      []
    );

  useEffect(() => {
    setFieldValue("result", generateQueryResult());
  }, [values.Query]);

  function generateQueryResult() {
    var result = "";
    let firstQuery = true;
    Object.entries(values.Query).map(([key, value]) => {
      const isArray = Array.isArray(value);

      if (isArray) {
        for (var val of value) {
          if (!val) return;
          result += firstQuery ? "?" : "&";
          const isNot = key.indexOf("Not") === 0;
          result += `${isNot ? key.replace("Not", "") : key}=${
            isNot ? "!" : ""
          }${val}`;
          firstQuery = false;
        }
      } else {
        if (value == null || value == "") return;
        result += firstQuery ? "?" : "&";
        const isNot = key.indexOf("Not") === 0;
        result += `${isNot ? key.replace("Not", "") : key}=${
          isNot ? "!" : ""
        }${value}`;
        firstQuery = false;
      }
    });
    return result;
  }

  function onReset() {
    setFieldValue("Query", {});
  }

  const uidOnChange = (uid: string) => {
    setFieldValue("Query", { uid });
  };

  return (
    <>
      <Box width="100%" maxW="800px" alignSelf="flex-start">
        <FormStack>
          {/* <TextArea label="Result" name="result" isReadOnly={true} />
          <Divider /> */}

          <VStack w="100%" spacing={4}>
            <HStack w="100%" alignItems="flex-start">
              <TextField
                label="Uid"
                name="Query.uid"
                onChange={(e) => uidOnChange(e.target.value)}
              />

              <ChakraReactSelectField
                label="Business Id"
                name="Query.businessId"
                selectedOptionColor="blue"
                isLoading={businessOptionsLoading}
                allowEmpty="Please select.."
                options={
                  businessOptions?.Results?.map((item) => {
                    return {
                      label: item.DisplayName!,
                      value: item.Meta?.Id!,
                    };
                  }) || []
                }
                isDisabled={
                  !(values.Query.uid == "" || values.Query.uid == null)
                }
              />
            </HStack>

            <HStack w="100%" alignItems="flex-start">
              <TextField
                label="Email Address Contains"
                name="Query.emailAddressContains"
                isDisabled={
                  !(values.Query.uid == "" || values.Query.uid == null)
                }
              />

              <TextField
                label="Phone Number Contains"
                name="Query.phoneNumberContains"
                isDisabled={
                  !(values.Query.uid == "" || values.Query.uid == null)
                }
              />
            </HStack>

            <HStack w="100%" alignItems="flex-start">
              <Box flex={16}>
                <TextField
                  label="Display Name Contains"
                  name="Query.displayNameContains"
                  isDisabled={
                    !(values.Query.uid == "" || values.Query.uid == null)
                  }
                />
              </Box>
              <Box flex={7}>
                <ChakraReactSelectField
                  label="Is Disabled"
                  name="Query.isDisabledEquals"
                  selectedOptionColor="blue"
                  options={[
                    { label: "All", value: null },
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  isDisabled={
                    !(values.Query.uid == "" || values.Query.uid == null)
                  }
                />
              </Box>

              <Box flex={9}>
                <ChakraReactSelectField
                  label="Include Service Account"
                  name="Query.includeServiceAccount"
                  selectedOptionColor="blue"
                  options={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: null },
                  ]}
                  isDisabled={
                    !(values.Query.uid == "" || values.Query.uid == null)
                  }
                />
              </Box>
            </HStack>

            <HStack w="100%" alignItems="flex-start">
              <TextField
                label="Custom Claim Keys"
                infoText="Enter a single value or many comma-separated values. i.e. businessId, integrationId"
                name="Query.customClaimKeys"
                isDisabled={
                  !(values.Query.uid == "" || values.Query.uid == null)
                }
              />

              <TextField
                label="Custom Claim Value Contains"
                name="Query.customClaimValueContains"
                isDisabled={
                  values.Query.customClaimKeys == null ||
                  values.Query.customClaimKeys == "" ||
                  !(values.Query.uid == "" || values.Query.uid == null)
                }
              />
            </HStack>
          </VStack>

          <HStack width="100%" justifyContent="start" spacing="3" pt={4} pb={6}>
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
            >
              Save
            </Button>

            <Button
              disabled={isSubmitting}
              onClick={onReset}
              colorScheme="cherryButton"
              bgColor="cherryButtonDark.50"
            >
              Reset
            </Button>

            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>
    </>
  );
};

export const UserFilterQueryForm = withFormik<
  UserFilterQueryFormProps,
  UserFilterQueryFormValues
>({
  mapPropsToValues: ({ query }) => {
    let result: UserFilterQueryFormValues = {
      Query: query ?? {},
    };

    return result;
  },

  handleSubmit: async (values, { props, setFieldError }) => {
    let result: { [key: string]: any } = {};
    Object.entries(values.Query).map(([key, value]) => {
      if (value) result[key] = value;
    });
    props.onSuccess(result);
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,

  validationSchema: Yup.object({
    Query: Yup.object({
      customClaimKeys: Yup.string().test(function (value) {
        const { customClaimValuesContains } = this.parent;
        if (customClaimValuesContains)
          return value != null
            ? true
            : this.createError({
                message: "Custom Claim Keys is required!",
                path: "Query.customClaimKeys",
              });
        return true;
      }),
    }),
  }),
})(InnerForm);
